import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Shield, ScrollText, Briefcase, Settings, Landmark, Clock } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { EmptyState, UploadButton } from './RecordHelpers';

const GOV_CATEGORIES = [
  { key: 'all', label: 'All' },
  { key: 'credentials', label: 'Corporate Credentials' },
  { key: 'bylaws', label: 'By-Laws' },
  { key: 'strategy', label: 'Organizational Strategy' },
  { key: 'policies', label: 'Policies' },
  { key: 'processes', label: 'Office Processes' },
];

export const GovernanceTab = ({ credentials, governanceRecords, term, govSubFilter, setGovSubFilter, onUploadOpen }) => {
  const getGovItems = () => {
    const creds = credentials.map(c => ({ ...c, _govType: 'credentials', _name: c.title || c.name, _desc: c.description }));
    const recs = governanceRecords.map(r => ({ ...r, _govType: r.record_type === 'charter' ? 'bylaws' : 'policies', _name: r.title, _desc: '' }));
    const all = [...creds, ...recs];
    const filtered = govSubFilter === 'all' ? all : all.filter(i => i._govType === govSubFilter);
    return filtered.filter(i => (i._name || '').toLowerCase().includes(term));
  };

  const items = getGovItems();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex flex-wrap gap-1.5">
          {GOV_CATEGORIES.map(cat => (
            <Button key={cat.key} variant={govSubFilter === cat.key ? 'default' : 'outline'} size="sm"
              className={`h-7 text-xs ${govSubFilter === cat.key ? 'bg-[#00A3A3] hover:bg-[#008585] text-white' : 'border-[#E2E6EA]'}`}
              onClick={() => setGovSubFilter(cat.key)} data-testid={`gov-filter-${cat.key}`}>
              {cat.label}
            </Button>
          ))}
        </div>
        <UploadButton tab="governance" defaultType="policy" defaultGov={govSubFilter !== 'all' ? govSubFilter : ''} onOpen={onUploadOpen} />
      </div>
      {items.length === 0 ? (
        <EmptyState icon={Landmark} message={term ? 'No matches' : `No ${govSubFilter === 'all' ? 'governance' : GOV_CATEGORIES.find(c => c.key === govSubFilter)?.label} items yet`}
          sub={govSubFilter === 'credentials' ? 'Manage credentials in Secretariat Settings' : govSubFilter === 'bylaws' ? 'Upload by-laws documents from Secretariat Settings' : 'Items will appear as they are created'} />
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {items.map(item => (
            <Card key={item.id} className="border-[#E2E6EA]" data-testid={`gov-item-${item.id}`}>
              <CardContent className="p-4 flex items-start gap-3">
                <div className="p-2 bg-[#F0F2F4] rounded-lg shrink-0">
                  {item._govType === 'credentials' ? <Shield className="h-5 w-5 text-[#00A3A3]" /> :
                   item._govType === 'bylaws' ? <ScrollText className="h-5 w-5 text-[#6366f1]" /> :
                   item._govType === 'strategy' ? <Briefcase className="h-5 w-5 text-[#F58A26]" /> :
                   item._govType === 'processes' ? <Settings className="h-5 w-5 text-[#5A6570]" /> :
                   <Landmark className="h-5 w-5 text-[#00A3A3]" />}
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-semibold text-[#1A1D21] truncate">{item._name}</h4>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="text-[10px]">{GOV_CATEGORIES.find(c => c.key === item._govType)?.label || item._govType}</Badge>
                    {item.credential_type && <span className="text-[10px] text-[#A8B1B9] capitalize">{item.credential_type.replace('_', ' ')}</span>}
                    {item.expiry_date && <span className="text-[10px] text-amber-600 flex items-center gap-0.5"><Clock className="h-3 w-3" />Exp: {format(parseISO(item.expiry_date), 'MMM d, yyyy')}</span>}
                  </div>
                  {item._desc && <p className="text-xs text-[#5A6570] mt-1 line-clamp-2">{item._desc}</p>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
